/* Vipps Hurtigkasse — Proffvolt
 *
 * Two modes (single file, both initialized together):
 *
 * 1. PRODUCT mode — <a data-pv-hurtigkasse-link>
 *    Keeps href in sync with selected variant + quantity stepper.
 *    Format: /apps/hurtigkasse/auth/start?variant=<ID>&qty=<N>
 *    Re-syncs on:
 *      - DOMContentLoaded (initial)
 *      - 'variant:change' event (Dawn variant picker)
 *      - change on [name="id"] (hidden input fallback)
 *      - change/input on [name="quantity"] (qty stepper)
 *
 * 2. CART mode — <button data-pv-hurtigkasse-cart>
 *    On click: fetches /cart.js, builds ?items=ID1:QTY1,ID2:QTY2,..., redirects.
 *    Always reflects current cart state (no stale snapshot from render time).
 *    Double-click guard via aria-disabled.
 *
 * Public API:
 *    window.PVHurtigkasse.init() — re-scan DOM (called by mini-cart.js
 *    rebindAfterRender() after every sections-API re-render so newly inserted
 *    cart buttons get bound).
 */
(function () {
  'use strict';

  var APP_PROXY_PATH = '/apps/hurtigkasse/auth/start';
  var BOUND_FLAG = 'pvHurtigkasseBound';

  // -- Ad-click attribution ---------------------------------------------------
  // Hurtigkasse rebuilds the cart server-side (Storefront API), which would
  // otherwise drop the gclid a normal add-to-cart keeps. Mirror the resolution
  // in snippets/gclid-capture.liquid (URL > localStorage 'pv_ad_click' > cookie)
  // + the GA4 client id, and append them to the auth/start URL so the app can
  // stamp them onto the cart -> order note_attributes -> Stape -> Google Ads.
  var PV_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'gad_source', 'gad_campaignid'];

  function pvReadCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\\/+^]/g, '\\$&') + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function pvParseStore(raw) {
    try { var o = JSON.parse(raw); return (o && typeof o === 'object') ? o : {}; } catch (e) { return {}; }
  }
  function pvGaClientId() {
    var raw = pvReadCookie('_ga'); // "GA1.1.<a>.<b>" -> client id "<a>.<b>"
    if (!raw) return null;
    var p = raw.split('.');
    return p.length >= 4 ? p.slice(-2).join('.') : null;
  }
  // Returns "" or "&gclid=...&_ga=..." — safe to concatenate onto any auth/start URL.
  function pvClickAttrQuery() {
    var url = {};
    try {
      var params = new URLSearchParams(window.location.search);
      PV_CLICK_KEYS.forEach(function (k) { var v = params.get(k); if (v) url[k] = v; });
    } catch (e) {}
    var ls = {};
    try { var lr = window.localStorage && localStorage.getItem('pv_ad_click'); if (lr) ls = pvParseStore(lr); } catch (e) {}
    var ck = pvParseStore(pvReadCookie('pv_ad_click') || '');
    var out = {};
    PV_CLICK_KEYS.forEach(function (k) { var v = url[k] || ls[k] || ck[k]; if (v) out[k] = v; });
    var ga = pvGaClientId();
    if (ga) out['_ga'] = ga;
    var s = '';
    for (var k in out) {
      if (Object.prototype.hasOwnProperty.call(out, k) && out[k]) {
        s += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(out[k]);
      }
    }
    return s;
  }

  // -- Product mode -----------------------------------------------------------

  function buildProductUrl(variantId, qty) {
    if (!variantId) return APP_PROXY_PATH;
    var q = parseInt(qty, 10);
    if (!(q > 0)) q = 1;
    return APP_PROXY_PATH + '?variant=' + encodeURIComponent(variantId) + '&qty=' + q + pvClickAttrQuery();
  }

  function syncProductLinks() {
    var links = document.querySelectorAll('[data-pv-hurtigkasse-link]');
    if (!links.length) return;

    links.forEach(function (link) {
      // Find the closest /cart/add form, or fall back to the first one on the page.
      var form = link.closest('form[action*="/cart/add"]') ||
                 document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      var variantId = idInput && idInput.value;
      var qty = (qtyInput && qtyInput.value) || 1;

      if (variantId) {
        link.setAttribute('href', buildProductUrl(variantId, qty));
        link.removeAttribute('aria-disabled');
      } else {
        link.setAttribute('aria-disabled', 'true');
      }
    });
  }

  // -- Cart mode --------------------------------------------------------------

  function handleCartClick(e) {
    var btn = e.currentTarget;
    if (btn.getAttribute('aria-disabled') === 'true') return;
    btn.setAttribute('aria-disabled', 'true');

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          btn.removeAttribute('aria-disabled');
          return;
        }
        var items = cart.items
          .map(function (i) { return i.variant_id + ':' + i.quantity; })
          .join(',');
        window.location.href = APP_PROXY_PATH + '?items=' + encodeURIComponent(items) + pvClickAttrQuery();
      })
      .catch(function (err) {
        if (window.console && console.error) {
          console.error('PVHurtigkasse cart fetch failed', err);
        }
        btn.removeAttribute('aria-disabled');
      });
  }

  function bindCartButtons() {
    var btns = document.querySelectorAll('[data-pv-hurtigkasse-cart]');
    btns.forEach(function (btn) {
      if (btn.dataset[BOUND_FLAG] === '1') return;
      btn.dataset[BOUND_FLAG] = '1';
      btn.addEventListener('click', handleCartClick);
    });
  }

  // -- Init -------------------------------------------------------------------

  function initAll() {
    syncProductLinks();
    bindCartButtons();
  }

  // Public API for mini-cart re-init after sections API refresh.
  window.PVHurtigkasse = { init: initAll };

  // Delegated listeners — survive DOM mutations from variant picker / mini-cart
  // re-renders. We attach to document once and stay.
  document.addEventListener('variant:change', syncProductLinks);
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('[name="id"], [name="quantity"]')) {
      syncProductLinks();
    }
  });
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('[name="quantity"]')) {
      syncProductLinks();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
